/**
 * Les gestes de la page d'une offre ouverte par son lien, et ses deux écritures : l'accepter, la
 * masquer. Par `hash` (le lien d'un mail, sans compte requis côté serveur) la porte `…ByHash` ; par
 * id, la porte de la liste.
 *
 * Port de `GroupAttributionProposalScreen` et `SubstitutionProposalScreen` :
 *  - « Accepter la proposition » ouvre la confirmation (« Je candidate », « Je confirme que
 *    j'accepte ») ; « Masquer la proposition » part sans confirmation ;
 *  - la réponse reçue, la boîte « Succès » dit ce qui s'est passé, et « OK » revient d'où l'on vient
 *    (l'accueil pour un lien ouvert à froid) ; par id, la liste qui porte l'offre est relue ;
 *  - un refus se dit dans la confirmation (accepter), ou dans une boîte « Erreur » (masquer).
 * Mesurés : `offer.accept` (ouvert avec la confirmation), `offer.hide`.
 */
import { useReducer } from "react"

import { groupAttributionProposalsService } from "../../../../../../data/services/groupAttributionProposalsService"
import { substitutionProposalsService } from "../../../../../../data/services/substitutionProposalsService"
import { type TutorOfferLink } from "../../../../view-models/tutor/Availability/tutorOfferViewModel"
import { useBackOr } from "../../../hooks/useBackOr"
import { type UITutorOffer } from "../../../types/tutorDispoOffres"
import { type TutorOfferAnswer, useTutorOfferAnswer } from "./useTutorOfferAnswers"
import type { TutorOfferFlowProps } from "../../../../../../design-system/templates/TutorOfferFlow"

const SURFACE = "tutor_offer_link"

const BY_ID = {
    group: groupAttributionProposalsService.updateGroupAttributionProposalStatus,
    substitution: substitutionProposalsService.updateSubstitutionProposalStatus,
}
const BY_HASH = {
    group: groupAttributionProposalsService.updateGroupAttributionProposalStatusByHash,
    substitution: substitutionProposalsService.updateSubstitutionProposalStatusByHash,
}

/** Les mots de la boîte « Succès », ceux des deux anciens écrans. */
export function tutorOfferSuccessMessage(kind: TutorOfferLink["kind"], answer: TutorOfferAnswer): string {
    if (answer !== "accept") return "La proposition a été masquée."
    return kind === "substitution"
        ? "La proposition a été acceptée !"
        : "La proposition a été acceptée avec succès ! Tu recevras une confirmation si le groupe est validé pour toi."
}

type PageUi = {
    confirming: boolean
    sending: "accept" | "hide" | null
    flowError: string | null
    errorMessage: string | null
    successMessage: string | null
}

type PageUiAction =
    | { type: "OPEN_CONFIRM" }
    | { type: "CLOSE_CONFIRM" }
    | { type: "SENDING"; answer: "accept" | "hide" }
    | { type: "SENT"; message: string }
    | { type: "FAILED"; answer: TutorOfferAnswer; message: string }
    | { type: "DISMISS_ERROR" }

const INITIAL: PageUi = { confirming: false, sending: null, flowError: null, errorMessage: null, successMessage: null }

function tutorOfferPageReducer(state: PageUi, action: PageUiAction): PageUi {
    switch (action.type) {
        case "OPEN_CONFIRM":
            return { ...state, confirming: true, flowError: null }
        case "CLOSE_CONFIRM":
            return { ...state, confirming: false, flowError: null }
        case "SENDING":
            return { ...state, sending: action.answer, flowError: null, errorMessage: null }
        case "SENT":
            return { ...state, sending: null, confirming: false, successMessage: action.message }
        case "FAILED":
            // Accepter se refuse dans sa confirmation, encore ouverte ; masquer n'en a pas.
            return action.answer === "accept" && state.confirming
                ? { ...state, sending: null, flowError: action.message }
                : { ...state, sending: null, errorMessage: action.message }
        case "DISMISS_ERROR":
            return { ...state, errorMessage: null }
        default:
            return state
    }
}

export function useTutorOfferState(offer: UITutorOffer | null, link: TutorOfferLink, reloadList: () => void) {
    const [ui, dispatchUi] = useReducer(tutorOfferPageReducer, INITIAL)
    const back = useBackOr("menu")

    const callbacks = {
        onSuccess: (answer: TutorOfferAnswer) => {
            dispatchUi({ type: "SENT", message: tutorOfferSuccessMessage(link.kind, answer) })
            reloadList()
        },
        onError: (answer: TutorOfferAnswer, message: string) => dispatchUi({ type: "FAILED", answer, message }),
    }
    // Par lien de mail, la porte du hash (sans portée : une seule offre) ; sinon celle de la liste.
    const service = link.hash != null ? BY_HASH[link.kind] : BY_ID[link.kind]
    const scopeParam = link.hash != null ? undefined : "id"
    const answers = {
        accept: useTutorOfferAnswer("accept", service, SURFACE, scopeParam, callbacks),
        hide: useTutorOfferAnswer("hide", service, SURFACE, scopeParam, callbacks),
    }

    const send = (answer: "accept" | "hide") => {
        if (ui.sending != null) return
        const sent =
            link.hash != null ? answers[answer].start({}, { hash: link.hash }) : answers[answer].start({ id: link.id })
        if (sent) dispatchUi({ type: "SENDING", answer })
    }

    const flow: TutorOfferFlowProps | null =
        offer != null && ui.confirming
            ? {
                  visible: true,
                  onClose: () => dispatchUi({ type: "CLOSE_CONFIRM" }),
                  offer,
                  step: "confirm",
                  entry: "page",
                  onConfirm: () => send("accept"),
                  sending: ui.sending === "accept" ? "accept" : null,
                  errorMessage: ui.flowError ?? undefined,
              }
            : null

    return {
        onBack: back,
        onAccept: () => {
            answers.accept.opened()
            dispatchUi({ type: "OPEN_CONFIRM" })
        },
        onHide: () => send("hide"),
        sending: ui.sending,
        flow,
        successMessage: ui.successMessage,
        onDismissSuccess: back,
        errorMessage: ui.errorMessage,
        onDismissError: () => dispatchUi({ type: "DISMISS_ERROR" }),
    }
}
